/**
 * Boards Layout
 *
 * Shared chrome for the /boards routes. Renders a PillToggle that switches
 * between every board defined in the `BOARDS` manifest registry — Tasks,
 * Product, Accounts, plus anything added later. The switcher doubles as the
 * page heading (left-aligned in place of a redundant title); the selected
 * child route fills the remaining height below it.
 *
 * Full-height flex column so the active board can fill to the bottom of the
 * viewport with a small gutter — no page scroll; vertical scroll lives inside
 * the board columns.
 *
 * @module features/boards/BoardsLayout
 */

import React from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { PillToggle } from '@chatsdet/ui';
import { ClipboardText, Lightbulb, Buildings } from '@phosphor-icons/react';
import { ALL_BOARDS, type BoardType } from '@chatsdet/cards-plugins';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';

const ICONS: Record<BoardType, React.ReactNode> = {
  agent_task: <ClipboardText size={12} weight="bold" />,
  product_item: <Lightbulb size={12} weight="bold" />,
  account: <Buildings size={12} weight="bold" />,
};

const PILL_COLORS: Partial<Record<BoardType, 'blue' | 'gray' | 'green'>> = {
  product_item: 'blue',
  account: 'green',
};

export function BoardsLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const active =
    ALL_BOARDS.find((b) => pathname.startsWith(`/boards/${b.slug}`)) ?? ALL_BOARDS[0];

  const handleChange = (next: BoardType) => {
    if (next === active.type) return;
    const target = ALL_BOARDS.find((b) => b.type === next);
    if (!target) return;
    void navigate({ to: `/boards/${target.slug}` });
  };

  const breadcrumbs = React.useMemo(
    () => [
      { label: 'Home', onPress: () => navigate({ to: '/' }) },
      { label: active.label, onPress: () => navigate({ to: `/boards/${active.slug}` }) },
    ],
    [navigate, active.label, active.slug]
  );
  useBreadcrumbs(breadcrumbs);

  return (
    <div className="flex min-h-0 flex-1 flex-col p-6">
      <div className="mb-4 shrink-0">
        <PillToggle<BoardType>
          aria-label="Select board"
          value={active.type}
          onChange={handleChange}
          options={ALL_BOARDS.map((b) => ({
            value: b.type,
            label: b.label,
            icon: ICONS[b.type],
            color: PILL_COLORS[b.type],
          }))}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>
    </div>
  );
}
